import { useQuery } from '@tanstack/react-query';
import type { Json } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';
import { NETWORK_DOC_KINDS, NETWORK_DOC_TITLES, networkDocKey, type NetworkDocKind } from '../lib/networkDocs';

export interface NetworkDocument {
  id: string;
  s3_key: string;
  title: string;
  metadata: Json | null;
  updated_at: string;
  trip_id: string | null;
}

/** Explicit column list; documents rows carry nothing customer-sensitive beyond metadata. */
export const NETWORK_DOC_COLUMNS = 'id, s3_key, title, metadata, updated_at, trip_id';

export type NetworkDocuments = Partial<Record<NetworkDocKind, NetworkDocument>>;

export function networkDocumentsQueryKey(locationId: string | undefined) {
  return ['network-docs', locationId] as const;
}

/** The three structured notes for a location, loaded in one request. */
export function useNetworkDocuments(locationId: string | undefined) {
  return useQuery({
    queryKey: networkDocumentsQueryKey(locationId),
    enabled: !!locationId,
    queryFn: async (): Promise<NetworkDocuments> => {
      if (!locationId) throw new Error('Location ID required');
      const keys = NETWORK_DOC_KINDS.map((kind) => networkDocKey(kind, locationId));
      const { data, error } = await supabase
        .from('documents')
        .select(NETWORK_DOC_COLUMNS)
        .in('s3_key', keys);
      if (error) throw error;
      const byKind: NetworkDocuments = {};
      for (const row of (data ?? []) as NetworkDocument[]) {
        for (const kind of NETWORK_DOC_KINDS) {
          if (row.s3_key === networkDocKey(kind, locationId)) byKind[kind] = row;
        }
      }
      return byKind;
    },
    staleTime: 60 * 1000,
  });
}

export interface SaveNetworkDocumentInput {
  kind: NetworkDocKind;
  locationId: string;
  tripId: string;
  /** From the trip's customer relation — never an empty string. */
  customerId: string;
  /** public.users.id of the technician, when the profile resolved. */
  uploadedByUserId: string | null;
  metadata: Json;
}

/**
 * Upsert keyed on s3_key. Resolves with the stored row so callers can confirm
 * the write landed; rejects with the PostgREST error otherwise.
 */
export async function saveNetworkDocument(input: SaveNetworkDocumentInput): Promise<{ id: string; updated_at: string }> {
  const { data, error } = await supabase
    .from('documents')
    .upsert(
      {
        customer_id: input.customerId,
        location_id: input.locationId,
        trip_id: input.tripId,
        title: NETWORK_DOC_TITLES[input.kind],
        category: 'network_diagram',
        s3_key: networkDocKey(input.kind, input.locationId),
        is_customer_visible: false,
        mime_type: 'application/json',
        uploaded_by_user_id: input.uploadedByUserId,
        metadata: input.metadata,
      },
      { onConflict: 's3_key' },
    )
    .select('id, updated_at')
    .single();
  if (error) throw error;
  return data;
}
